/* eslint-disable no-nested-ternary */
/* eslint-disable multiline-ternary */
/* eslint-disable react/no-danger */
// TODO: remove cheerio

import React from 'react'
import { convertToSlug } from 'src/utils/IdGenerator'
import Carousel from '@components/Carousel/carousel'
import ErrorBoundary from '@components/ErrorBoundary/errorBoundary'
import TabComponent from '@components/NTabs/NTabs'
import Image from 'next/image'
import assetLoader from '@utils/internalImageLoader'
import { getClassNames } from '@helpers/productWidgetHelper'
import { getHeadingDesc } from '@utils/schemaUtils'
import { HTMLTagsForHowTo } from '@configs/howToHTMLTag'
import { getGroupedSectionCardsMap, convertToClassName } from './utils'
import styles from './ncolumn.module.scss'
import NColumnGridCta from './NColumnGridCta'

const cheerio = typeof window === 'undefined' ? global.cheerio : window.cheerio

// modify HTML for img tags

const getModifiedHtml = tableContent => {
  if (!tableContent) return ''
  const $ = cheerio.load(tableContent, null, false)

  // Remove <p> tags with only non-breaking space content
  $('p').each((_index, element) => {
    const $p = $(element)
    if ($p.html().trim() === '&nbsp;') {
      $p.remove()
    }
  })

  $('img').attr('loading', 'lazy')
  $('img').attr('decoding', 'async')

  $('a').each((_index, element) => {
    const $a = $(element)
    $a.attr('target', '_blank')
  })

  const modifiedHTML = $.html()
  return modifiedHTML
}

const getHowToSchema = (sectionCards, heading, description): object => {
  const steps = (sectionCards ?? []).map((card, i) => {
    const cardHead = getHeadingDesc(card.cardContent, true)
    return {
      '@type': 'HowToStep',
      position: i + 1,
      name: cardHead?.heading || `Step ${i + 1}`,
      text: cardHead?.description,
    }
  })
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: heading,
    description,
    step: steps,
  }
}

const NColumnGrid = ({ data }) => {
  const contentHeavyFlag = data?.contentHeavyFlag
  const { sectionHeading, sectionVariantForStyling, sectionColumnLayout, sectionCards, sectionCTA, hasTabs } =
    data?.data?.attributes ?? {}

  // Generate dynamic class names based on the API response
  const containerClassName = getClassNames([styles.container, styles[sectionVariantForStyling]], {
    [styles.adjustPaddingMargin]: contentHeavyFlag,
  })

  const sectionHeadingClassName = getClassNames([styles.sectionHeading], {
    [styles.adjustAlignment]: contentHeavyFlag,
  })
  const gridClassName = `${styles.grid} ${styles[`columns-${sectionColumnLayout}`]}`

  // States/Data needed
  const mappedValuesForTabs = getGroupedSectionCardsMap(sectionCards)

  // Generate all the classes - dynamic
  const generateClassName = item => {
    const tabSectionClassName = item.tabSection ? convertToClassName(item.tabSection) : ''
    const cardTypeClassName = styles[item.cardType]
    const fullWidthImageClassName = item.fullWidthImage === 'true' ? styles.FullWidthImage : styles.notFullWidthImage
    const cardNoCta = item?.cta?.length === 0 ? styles.noCtaInCard : ''

    return `${tabSectionClassName} ${styles.card} ${cardTypeClassName} ${cardNoCta} ${fullWidthImageClassName}`
  }

  // for id assign
  const h2Regex = /<h2[^>]*>(.*?)<\/h2>/
  const matches = data?.data?.attributes?.sectionHeading?.match(h2Regex)
  const h2Content = matches ? matches[1] : null
  const idContent = data?.data?.attributes?.headerTabName || h2Content

  const isWhiteBg = sectionVariantForStyling === 'plainBackground'

  const heading = getHeadingDesc(sectionHeading)
  const isHowToComp =
    heading.heading.toLowerCase().includes('how to') || heading.heading.toLowerCase().includes('steps')
  const { WrapperTag, ItemTag } = HTMLTagsForHowTo.nColGrid(isHowToComp)

  const howToSchema = isHowToComp ? getHowToSchema(sectionCards ?? [], heading.heading, heading.description) : null
  return (
    <ErrorBoundary data={data}>
      <div className={`${styles.rootContainer} ${isWhiteBg && 'white'}`} id={convertToSlug(idContent)}>
        {howToSchema && (
          <script
            type="application/ld+json"
            id={`howto-schema-${heading.heading.replace(/\s+/g, '-')}`}
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(howToSchema),
            }}
          />
        )}
        {/* Section ColorBlend background - Not a simple Linear Gradient */}
        {sectionVariantForStyling === 'threeColorBlendSection' && (
          <div className={styles.colorBlendParent}>
            <div className={styles.colorBlendTopRightCircle} />
            <div className={styles.colorBlendBottomLeftCircle} />
          </div>
        )}
        <div className={containerClassName}>
          {/* Section heading/decription */}
          <div
            className={sectionHeadingClassName}
            dangerouslySetInnerHTML={{ __html: getModifiedHtml(sectionHeading) }}
          />
          {/* Tab Component to render all the tabs */}
          {hasTabs && (
            <TabComponent
              activeStyles={styles.active}
              inactiveStyles={styles.inactive}
              groupedSectionCardsMap={mappedValuesForTabs}
              sectionVariantForStyling={sectionVariantForStyling}
            />
          )}

          {/* Grid to render all the cards */}
          <WrapperTag className={gridClassName}>
            {sectionColumnLayout === 0 && sectionCards?.length ? (
              <Carousel cardsToRender={sectionCards} carouselId={sectionCards[0]?.id} />
            ) : (
              // Rendering All Cards - Dynamic
              sectionCards?.map((item, _index) => {
                const modifiedCardContent = getModifiedHtml(item.cardContent)
                return (
                  <ItemTag
                    key={`${item.cardContent}${_index}`}
                    className={generateClassName(item)}
                    data-key={item.tabSection}>
                    {/* Render the HTML received as response for Card */}
                    <span dangerouslySetInnerHTML={{ __html: modifiedCardContent }} />
                    <NColumnGridCta
                      data={item.cta?.[0]}
                      withinCard
                      segmentEventCommonProperties={data?.segmentEventCommonProperties}
                    />

                    {/* Render the card tiles if present */}
                    {item?.cardSelectionTiles?.length > 0 && (
                      <div className={styles.selectionTiles}>
                        {item?.cardSelectionTiles?.map((tile, index) => (
                          <div className={styles.selectionTile} key={`${tile?.optionCardName}-${index}`}>
                            <span className={styles.selectionHeading}>{tile?.optionCardHeading}</span>
                            <span className={styles.selectionSubHeading}>{tile?.optionCardSubHeading}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Card/Section Pattern */}
                    {item?.cardType?.includes('CirclePattern') ? (
                      <div className={styles['circle-pattern']}>
                        <Image
                          src={`${assetLoader()}/images/circle-carousel.svg`}
                          alt="Carousel Circle Pattern"
                          width={173}
                          height={172}
                          loading="lazy"
                        />
                      </div>
                    ) : null}
                  </ItemTag>
                )
              })
            )}
          </WrapperTag>
          {sectionCTA && (
            <div className={styles.sectionCta}>
              {sectionCTA?.sectionCTAPara && (
                <span dangerouslySetInnerHTML={{ __html: getModifiedHtml(sectionCTA?.sectionCTAPara) }} />
              )}
              <NColumnGridCta data={sectionCTA} segmentEventCommonProperties={data?.segmentEventCommonProperties} />
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  )
}

export default NColumnGrid
